import { api } from "./api";

/**
 * Breed-standard curves (Phase 3b.2, extended in 3b.3).
 *
 * A curve is the expected value of a metric by age in weeks — what a flock is
 * benchmarked against. Resolution has FOUR possible outcomes, not three: the
 * flock's breed, then the farm-wide curve, then the generic curve shipped with
 * the app — and for the three gram metrics, nothing at all, because no generic
 * curve is shipped for them. `source` says which, so the UI can be honest
 * about whether the farm is measured against its own breeder guide, a default,
 * or nothing.
 */

export type CurveMetric = "hdp" | "mortality" | "body_weight" | "egg_weight" | "feed_intake";

/**
 * Where the curve in force came from.
 *
 * `"none"` means there is no curve at all: the farm has stored none and nothing
 * is shipped. Only the three gram metrics can be in that state — a generic
 * brown-layer production percentage is a usable approximation, but an averaged
 * body-weight curve is strain-specific to the point of being misleading, so no
 * default is shipped for them. Screens must handle it: rendering it as
 * `"default"` tells the farm it is being benchmarked against something when it
 * is not.
 */
export type CurveSource = "breed" | "farm" | "default" | "none";

export interface CurvePoint {
  age_weeks: number;
  value: number;
}

export interface StandardCurve {
  metric: CurveMetric;
  breed_id: number | null;
  source: CurveSource;
  /**
   * True only when rows exist at the level that was asked for. Asking for a
   * breed's curve when that breed has none returns the farm-wide points with
   * `source: "farm"` and `stored: false` — an inherited curve, with nothing
   * here to reset.
   */
  stored: boolean;
  points: CurvePoint[];
}

/**
 * Every metric, with the ceiling its values are validated against.
 *
 * `max` mirrors `model.CurveMetricMax` and the metric-aware CHECK in migration
 * 000067. It is here because the client validated `value > 100` for every
 * metric, which silently made two of the five unusable: a body weight in grams
 * is four digits, and the server rejected it too. The bound belongs to the
 * metric, not to the form.
 */
export const CURVE_METRICS: {
  value: CurveMetric;
  label: string;
  unit: string;
  max: number;
  help: string;
}[] = [
  {
    value: "hdp",
    label: "Hen-day production",
    unit: "%",
    max: 100,
    help: "Eggs per 100 live hens per day. Starts at lay onset — a flock younger than the first row has no standard.",
  },
  {
    value: "mortality",
    label: "Cumulative mortality",
    unit: "%",
    max: 100,
    help: "Total deaths since placement, as a share of birds housed. Can only rise with age.",
  },
  {
    value: "body_weight",
    label: "Body weight",
    unit: "g",
    max: 9000,
    help: "Average grams per bird, from a weighed sample. The earliest warning a farm gets — a pullet off its growth curve at week 12 is still correctable, and it will not show up in production for another six weeks.",
  },
  {
    value: "egg_weight",
    label: "Egg weight",
    unit: "g",
    max: 200,
    help: "Average grams per egg. Rises with age, so it is read as a curve rather than a target.",
  },
  {
    value: "feed_intake",
    label: "Feed intake",
    unit: "g/bird/day",
    max: 500,
    help: "Grams of feed per bird per day. Read against body weight: a flock eating to standard but growing below it is a different problem from one doing neither.",
  },
];

/** The ceiling for a metric's values. Falls back to a percentage. */
export const curveMetricMax = (m: CurveMetric): number =>
  CURVE_METRICS.find((x) => x.value === m)?.max ?? 100;

/** The unit a metric is stored in, for labels and messages. */
export const curveMetricUnit = (m: CurveMetric): string =>
  CURVE_METRICS.find((x) => x.value === m)?.unit ?? "";

export const curveMetricLabel = (m: CurveMetric): string =>
  CURVE_METRICS.find((x) => x.value === m)?.label ?? m;

const breedQuery = (breedId: number | null) =>
  breedId == null ? "" : `?breed_id=${breedId}`;

export const standardCurveService = {
  list: () => api.get<StandardCurve[]>("/standard-curves").then((r) => r.data),

  /** Returns the curve in force for this breed, falling back through the chain. */
  get: (metric: CurveMetric, breedId: number | null) =>
    api
      .get<StandardCurve>(`/standard-curves/${metric}${breedQuery(breedId)}`)
      .then((r) => r.data),

  save: (metric: CurveMetric, breedId: number | null, points: CurvePoint[]) =>
    api
      .put<StandardCurve>(`/standard-curves/${metric}`, {
        breed_id: breedId,
        points,
      })
      .then((r) => r.data),

  /** Removes the stored curve so the level below it applies again. */
  reset: (metric: CurveMetric, breedId: number | null) =>
    api.delete(`/standard-curves/${metric}${breedQuery(breedId)}`).then((r) => r.data),

  /**
   * Parses a CSV on the server and returns the points WITHOUT saving them, so
   * the owner can check what was read off the breeder guide before it replaces
   * what their flocks are measured against.
   */
  importCsv: (metric: CurveMetric, file: File) => {
    const form = new FormData();
    form.append("file", file);
    return api
      .post<{ metric: CurveMetric; points: CurvePoint[] }>(
        `/standard-curves/${metric}/import`,
        form,
        // Required. The shared axios instance defaults to application/json, and
        // axios then serializes FormData to JSON rather than sending multipart —
        // the server would reject a perfectly good file as "not a valid upload".
        // The other uploaders in this app pass the same header for this reason.
        { headers: { "Content-Type": "multipart/form-data" } },
      )
      .then((r) => r.data);
  },
};

/**
 * Client-side mirror of the server's curve validation, so the owner sees the
 * problem while typing rather than after a failed save. The server revalidates
 * — this is a convenience, not the guarantee.
 */
export function validateCurvePoints(
  metric: CurveMetric,
  points: CurvePoint[],
): string | null {
  if (points.length < 2) return "A curve needs at least 2 rows.";

  const sorted = [...points].sort((a, b) => a.age_weeks - b.age_weeks);
  for (let i = 0; i < sorted.length; i++) {
    const p = sorted[i];
    if (!Number.isInteger(p.age_weeks) || p.age_weeks < 0 || p.age_weeks > 200) {
      return `Week ${p.age_weeks} is outside 0-200.`;
    }
    // Per metric, not a flat 100. A flat bound rejected every real body-weight
    // and feed-intake figure, which made widening the server's bound pointless.
    const max = curveMetricMax(metric);
    if (!Number.isFinite(p.value) || p.value < 0 || p.value > max) {
      return `Value at week ${p.age_weeks} is outside 0-${max}${curveMetricUnit(metric)}.`;
    }
    if (i > 0 && sorted[i - 1].age_weeks === p.age_weeks) {
      return `Week ${p.age_weeks} appears twice.`;
    }
    if (metric === "mortality" && i > 0 && p.value < sorted[i - 1].value) {
      return `Cumulative mortality falls from ${sorted[i - 1].value}% at week ${
        sorted[i - 1].age_weeks
      } to ${p.value}% at week ${p.age_weeks}; it can only rise.`;
    }
  }
  return null;
}
